import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { buildExcelDocument } from '@/lib/excelExport';
import { useFinance } from '@/hooks/useFinance';
import { formatDate, formatMoney, inRange, periodRange, REVENUE_CATEGORIES, type Period } from '@/lib/finance';
import { REVENUE_SOURCE_META } from '@/lib/financeSync';
import { FinanceSection, FinanceEmpty, FinanceModal, Field, inputClass, PeriodFilter } from '../components/FinanceUI';

const PAID_STATUSES = ['paid', 'processing', 'shipped', 'delivered'];

export default function FinanceRevenusPage() {
  const finance = useFinance();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { currency, merchantId, transactions, accounts } = finance;
  const [period, setPeriod] = useState<Period>('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [collecting, setCollecting] = useState(false);
  const [creatingExcel, setCreatingExcel] = useState(false);

  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState(REVENUE_CATEGORIES[0]);
  const [description, setDescription] = useState('');
  const [counterparty, setCounterparty] = useState('');
  const [accountId, setAccountId] = useState<string>('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const range = useMemo(() => periodRange(period, customStart, customEnd), [period, customStart, customEnd]);
  const revenue = useMemo(
    () => transactions.filter((t) => t.direction === 'in' && inRange(t.occurred_at, range)),
    [transactions, range]
  );
  const total = revenue.reduce((s, t) => s + Number(t.amount), 0);

  const reset = () => {
    setAmount(''); setCategory(REVENUE_CATEGORIES[0]); setDescription(''); setCounterparty(''); setAccountId(''); setDate(new Date().toISOString().slice(0, 10));
  };

  const submit = async () => {
    const value = parseFloat(amount);
    if (!value || value <= 0) { setMsg('Montant invalide'); return; }
    setBusy(true);
    try {
      await finance.addTransaction({
        direction: 'in',
        amount: value,
        category,
        description: description || category,
        counterparty: counterparty || null,
        account_id: accountId ? Number(accountId) : null,
        occurred_at: new Date(date).toISOString(),
      });
      reset();
      setModalOpen(false);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const handleOpenInExcel = async () => {
    if (!user || revenue.length === 0) return;
    setCreatingExcel(true);
    try {
      const headers = ['Date', 'Catégorie', 'Description', 'Client / source', 'Montant', 'Devise'];
      const rows = revenue.map((t) => [
        formatDate(t.occurred_at),
        t.category || '',
        t.description || '',
        t.counterparty || '',
        String(Number(t.amount)),
        currency,
      ]);
      await buildExcelDocument(
        user.id,
        `Revenus ${new Date().toISOString().slice(0, 10)}`,
        headers,
        rows,
        { sheetName: 'Revenus' },
      );
      navigate('/dashboard/file-manager');
    } catch {
      /* erreur déjà gérée côté documents */
    } finally {
      setCreatingExcel(false);
    }
  };

  const collectOrders = async () => {
    if (!merchantId) return;
    setCollecting(true);
    setMsg(null);
    try {
      const { data: prods } = await supabase.from('product_items').select('id').eq('idcommerce', merchantId);
      const prodIds = new Set((prods || []).map((p: { id: number }) => String(p.id)));
      if (prodIds.size === 0) { setMsg('Aucun produit en catalogue à relier aux commandes.'); return; }

      const { data: items } = await supabase.from('order_items').select('order_id, product_id, subtotal, final_price, quantity');
      const byOrder: Record<number, number> = {};
      (items || []).forEach((it: { order_id: number; product_id: string; subtotal: number | null; final_price: number | null; quantity: number | null }) => {
        if (!prodIds.has(String(it.product_id))) return;
        const line = Number(it.subtotal ?? (Number(it.final_price ?? 0) * Number(it.quantity ?? 1)));
        if (Number.isNaN(line)) return;
        byOrder[it.order_id] = (byOrder[it.order_id] || 0) + line;
      });
      const orderIds = Object.keys(byOrder).map(Number);
      if (orderIds.length === 0) { setMsg('Aucune commande liée à vos produits.'); return; }

      const { data: headers } = await supabase.from('order_headers').select('id, status, created_at').in('id', orderIds);
      const existing = new Set(transactions.filter((t) => t.source === 'order').map((t) => t.source_ref));
      let created = 0;
      for (const h of (headers || []) as { id: number; status: string; created_at: string }[]) {
        if (!PAID_STATUSES.includes(String(h.status))) continue;
        const ref = `order:${h.id}`;
        if (existing.has(ref)) continue;
        await finance.addTransaction({
          direction: 'in',
          amount: byOrder[h.id],
          description: `Commande #${h.id}`,
          category: 'Commande',
          source: 'order',
          source_ref: ref,
          occurred_at: h.created_at,
        });
        created += 1;
      }
      setMsg(created > 0 ? `${created} commande(s) importée(s) dans les revenus.` : 'Aucune nouvelle commande à importer (pas de doublon créé).');
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Erreur lors de la collecte');
    } finally {
      setCollecting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <PeriodFilter value={period} onChange={setPeriod} customStart={customStart} customEnd={customEnd} onCustomChange={(s, e) => { setCustomStart(s); setCustomEnd(e); }} />
        <div className="flex items-center gap-2">
          <button onClick={collectOrders} disabled={collecting} className="flex items-center gap-1.5 px-3.5 py-2 bg-background-50 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-100 disabled:opacity-50 transition-colors cursor-pointer">
            {collecting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-refresh-line"></i>}
            Collecter les commandes
          </button>
          <button onClick={handleOpenInExcel} disabled={creatingExcel || revenue.length === 0} className="flex items-center gap-1.5 px-3.5 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer">
            {creatingExcel ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-file-excel-2-line"></i>}
            Ouvrir dans Excel
          </button>
          <button onClick={() => { reset(); setModalOpen(true); }} className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
            <i className="ri-add-line"></i>Ajouter un revenu
          </button>
        </div>
      </div>

      {msg && (
        <div className="px-4 py-2.5 rounded-lg bg-secondary-50 text-secondary-800 text-sm flex items-center gap-2">
          <i className="ri-information-line"></i>{msg}
        </div>
      )}

      <div className="bg-primary-50 border border-primary-200/60 rounded-lg p-4 flex items-center justify-between">
        <span className="text-sm text-primary-800">Revenus de la période</span>
        <span className="text-lg font-bold font-heading text-primary-700">{formatMoney(total, currency)}</span>
      </div>

      {/* Regroupement des revenus synchronisés par source */}
      {REVENUE_SOURCE_META.some((m) => (finance.revenueBySource[m.label] || 0) > 0) && (
        <FinanceSection title="Regroupement par source" icon="ri-pie-chart-2-line">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {REVENUE_SOURCE_META.map((meta) => {
              const amount = finance.revenueBySource[meta.label] || 0;
              if (amount <= 0) return null;
              return (
                <div key={meta.key} className="bg-background-50 border border-background-200/70 rounded-lg p-3.5 flex items-center gap-3">
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                    <i className={meta.icon}></i>
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-500 truncate">{meta.label}</p>
                    <p className="text-sm font-bold text-foreground-950">{formatMoney(amount, currency)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </FinanceSection>
      )}

      <FinanceSection title={`Revenus (${revenue.length})`} icon="ri-money-dollar-circle-line">
        {revenue.length === 0 ? (
          <FinanceEmpty
            icon="ri-money-dollar-circle-line"
            title="Aucun revenu sur cette période"
            hint="Ajoutez un revenu manuellement ou collectez automatiquement vos commandes payées."
          />
        ) : (
          <div className="divide-y divide-background-200/70">
            {revenue.map((t) => (
              <div key={t.id} className="flex items-center gap-3 py-3">
                <span className="w-9 h-9 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                  <i className={`${t.source === 'order' ? 'ri-shopping-cart-2-line' : 'ri-add-circle-line'} text-sm`}></i>
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground-900 truncate">{t.description || t.category || 'Revenu'}</div>
                  <div className="text-xs text-foreground-400 flex items-center gap-2 flex-wrap">
                    <span>{formatDate(t.occurred_at)}</span>
                    {t.category && <span className="px-1.5 py-0.5 rounded-full bg-secondary-50 text-secondary-700">{t.category}</span>}
                    {t.counterparty && <span><i className="ri-user-line"></i> {t.counterparty}</span>}
                  </div>
                </div>
                <span className="text-sm font-bold text-primary-700 whitespace-nowrap">+ {formatMoney(Number(t.amount), currency)}</span>
                <button
                  onClick={() => finance.reverseTransaction(t)}
                  title="Corriger (écriture inverse)"
                  className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-go-back-line text-sm"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </FinanceSection>

      {modalOpen && (
        <FinanceModal title="Ajouter un revenu" onClose={() => setModalOpen(false)}>
          <Field label="Montant *">
            <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass} placeholder="0.00" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Catégorie">
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
                {REVENUE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Date">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClass} />
            </Field>
          </div>
          <Field label="Client / source">
            <input value={counterparty} onChange={(e) => setCounterparty(e.target.value)} className={inputClass} placeholder="Nom du client (optionnel)" />
          </Field>
          <Field label="Description">
            <input value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} placeholder="Ex : Vente comptoir" />
          </Field>
          {accounts.length > 0 && (
            <Field label="Encaisser sur le compte">
              <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className={inputClass}>
                <option value="">— Aucun compte —</option>
                {accounts.map((a) => <option key={a.id} value={String(a.id)}>{a.name}</option>)}
              </select>
            </Field>
          )}
          <button onClick={submit} disabled={busy} className="w-full mt-2 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap">
            {busy ? <i className="ri-loader-4-line animate-spin"></i> : 'Enregistrer le revenu'}
          </button>
          <p className="text-[11px] text-foreground-400 mt-2 flex items-start gap-1">
            <i className="ri-shield-check-line mt-0.5"></i>
            Les revenus déjà collectés depuis une commande ne sont jamais dupliqués.
          </p>
        </FinanceModal>
      )}
    </div>
  );
}